import React, { useState, useEffect, useCallback } from 'react';
import {
  OTASignupsTab, ContactMessagesTab, EmailCenterTab, GoogleSettingsTab, SponsorsTab, EventEditorTab, CheerEditorTab,
} from './admin';
import LoginScreen from './admin/LoginScreen';
import AdminHeader from './admin/AdminHeader';
import AdminTabs from './admin/AdminTabs';
import InterestLeaderboard from './admin/InterestLeaderboard';
import { exportSignupsCSV, exportContactsCSV } from './admin/exportCsv';
import '../AdminPage.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatDate = (dateString) =>
  new Date(dateString).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });

const AdminPage = () => {
  // Auth — intentionally in-memory only. Persisting "is admin" in sessionStorage
  // would let any XSS grant itself admin in the attacker's session.
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const [activeTab, setActiveTab] = useState('signups');
  const [signups, setSignups] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortField, setSortField] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [staffProfiles, setStaffProfiles] = useState({ leadership: [], coaches: [], all: [] });

  const fetchStaffProfiles = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/staff/profiles`);
      const data = await response.json();
      setStaffProfiles(data);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.warn('Staff profiles fetch failed:', err.message);
    }
  }, []);

  const fetchSignups = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/ota-signups`);
      if (!response.ok) throw new Error('Failed to fetch signups');
      const data = await response.json();
      setSignups(data);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.warn('Signups fetch failed:', err.message);
    }
  }, []);

  const fetchContacts = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/contact`);
      if (!response.ok) throw new Error('Failed to fetch contacts');
      const data = await response.json();
      setContacts(data);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.warn('Contacts fetch failed:', err.message);
    }
  }, []);

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchSignups(), fetchContacts()]);
    setLoading(false);
  }, [fetchSignups, fetchContacts]);

  useEffect(() => {
    if (isAuthenticated) fetchStaffProfiles();
  }, [isAuthenticated, fetchStaffProfiles]);

  useEffect(() => {
    if (isAuthenticated) fetchAllData();
  }, [isAuthenticated, fetchAllData]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const unreadContacts = contacts.filter((c) => c.status === 'unread').length;

  if (!isAuthenticated) {
    return <LoginScreen onAuthenticated={() => setIsAuthenticated(true)} />;
  }

  return (
    <div className="admin-container">
      <AdminHeader onLogout={() => setIsAuthenticated(false)} />

      <main className="admin-main">
        <InterestLeaderboard contacts={contacts} />

        <AdminTabs
          activeTab={activeTab}
          onTabChange={setActiveTab}
          signupsCount={signups.length}
          unreadCount={unreadContacts}
        />

        {activeTab === 'signups' && (
          <OTASignupsTab
            signups={signups}
            loading={loading}
            sortField={sortField}
            sortOrder={sortOrder}
            onSort={handleSort}
            onRefresh={fetchAllData}
            onExport={() => exportSignupsCSV(signups, formatDate)}
            formatDate={formatDate}
          />
        )}

        {activeTab === 'contacts' && (
          <ContactMessagesTab
            contacts={contacts}
            setContacts={setContacts}
            loading={loading}
            sortOrder={sortOrder}
            onRefresh={fetchAllData}
            onExport={() => exportContactsCSV(contacts, formatDate)}
            formatDate={formatDate}
            staffProfiles={staffProfiles}
          />
        )}

        {activeTab === 'email' && <EmailCenterTab />}
        {activeTab === 'sponsors' && <SponsorsTab />}
        {activeTab === 'event' && <EventEditorTab />}
        {activeTab === 'cheer' && <CheerEditorTab />}
        {activeTab === 'settings' && <GoogleSettingsTab />}
      </main>
    </div>
  );
};

export default AdminPage;
